import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faSpinner, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import {
  approvalRequestTypeService,
  type ApprovalRequestType,
  type ApprovalRequestTypeAppliesTo,
  type CreateApprovalRequestTypePayload,
  type CustomerApprovalMode,
  type DefaultFirstReviewer,
} from '@/services/approval-request-type.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';
import { quotationInvariantError } from './approval-request-type-rules';

const PAGE_SIZE = 20;

/** The server's own message (e.g. the D-128 Q-6 refusal) when it sent one; else the fallback. */
function extractMsg(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

const APPLIES_TO_OPTIONS: { value: ApprovalRequestTypeAppliesTo; label: string }[] = [
  { value: 'PROCEDURE', label: 'Procedure' },
  { value: 'FINDING', label: 'Finding' },
  { value: 'COMMERCIAL_REVIEW', label: 'Commercial Review' },
  { value: 'QUOTATION', label: 'Quotation' },
  { value: 'WORK_ORDER', label: 'Work Order' },
  { value: 'SERVICE_REPORT', label: 'Service Report' },
  { value: 'GLOBAL', label: 'Global (all contexts)' },
];

const FIRST_REVIEWER_OPTIONS: { value: DefaultFirstReviewer; label: string }[] = [
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'MASTER_SERVICE_ENGINEER', label: 'Master Service Engineer' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'OWNER_ADMIN', label: 'Owner / Admin' },
];

const CUSTOMER_APPROVAL_LABELS: Record<CustomerApprovalMode, string> = {
  NOT_REQUIRED: 'Not Required',
  OPTIONAL: 'Optional',
  MANDATORY: 'Mandatory',
};

interface ApprovalRequestTypeFormProps {
  initial?: Partial<CreateApprovalRequestTypePayload>;
  onSubmit: (d: CreateApprovalRequestTypePayload) => void;
  onCancel: () => void;
  saving: boolean;
}

function ApprovalRequestTypeForm({ initial, onSubmit, onCancel, saving }: ApprovalRequestTypeFormProps) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [requiresCommercialReview, setRequiresCommercialReview] = useState(
    initial?.requiresCommercialReview ?? false,
  );
  // Phase 7 Slice 7A (D-128 Q-6): a quotation kind ALWAYS requires Commercial review.
  // Switching the quotation flag on sets the review flag and locks it; switching it
  // off releases the lock and leaves the review flag as it is. The server enforces
  // the same invariant and stays authoritative.
  const [requiresQuotation, setRequiresQuotation] = useState(initial?.requiresQuotation ?? false);
  const [allowsPartialApproval, setAllowsPartialApproval] = useState(
    initial?.allowsPartialApproval ?? true,
  );
  const [isBerType, setIsBerType] = useState(initial?.isBerType ?? false);
  const [portalExpiryDays, setPortalExpiryDays] = useState(
    String(initial?.portalExpiryDays ?? 14),
  );
  const [requiresConsent, setRequiresConsent] = useState(initial?.requiresConsent ?? false);
  const [customerCanRequestModification, setCustomerCanRequestModification] = useState(
    initial?.customerCanRequestModification ?? true,
  );
  const [appliesTo, setAppliesTo] = useState<ApprovalRequestTypeAppliesTo[]>(
    initial?.appliesTo ?? [],
  );
  const [defaultFirstReviewer, setDefaultFirstReviewer] = useState<DefaultFirstReviewer | ''>(
    initial?.defaultFirstReviewer ?? '',
  );
  const [customerApprovalMode, setCustomerApprovalMode] = useState<CustomerApprovalMode>(
    initial?.customerApprovalMode ?? 'NOT_REQUIRED',
  );
  const [allowRoutingOverride, setAllowRoutingOverride] = useState(
    initial?.allowRoutingOverride ?? true,
  );
  const [sortOrder, setSortOrder] = useState(String(initial?.sortOrder ?? 0));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const toggleAppliesTo = (val: ApprovalRequestTypeAppliesTo) => {
    setAppliesTo((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!code.trim()) errs.code = 'Code is required';
    if (!name.trim()) errs.name = 'Name is required';
    const expiry = parseInt(portalExpiryDays, 10);
    if (isNaN(expiry) || expiry < 1 || expiry > 365)
      errs.portalExpiryDays = 'Must be between 1 and 365';
    const order = parseInt(sortOrder, 10);
    if (isNaN(order) || order < 0) errs.sortOrder = 'Must be 0 or greater';
    const quotationErr = quotationInvariantError(requiresQuotation, requiresCommercialReview);
    if (quotationErr) errs.requiresQuotation = quotationErr;
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      requiresCommercialReview,
      requiresQuotation,
      allowsPartialApproval,
      isBerType,
      portalExpiryDays: expiry,
      requiresConsent,
      customerCanRequestModification,
      appliesTo,
      defaultFirstReviewer: defaultFirstReviewer || undefined,
      customerApprovalMode,
      allowRoutingOverride,
      sortOrder: order,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Code" htmlFor="art-code" required error={errors.code}>
          <Input id="art-code" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. COST, BER, PARTIAL" disabled={saving} maxLength={50} />
        </FormField>
        <FormField label="Sort Order" htmlFor="art-order" error={errors.sortOrder}>
          <Input id="art-order" type="number" min={0} value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)} disabled={saving} />
        </FormField>
      </div>
      <FormField label="Name" htmlFor="art-name" required error={errors.name}>
        <Input id="art-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Cost Approval" disabled={saving} maxLength={200} />
      </FormField>
      <FormField label="Description" htmlFor="art-desc">
        <Textarea id="art-desc" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description" rows={2} disabled={saving} />
      </FormField>

      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">Applies To</p>
        <p className="text-xs text-muted-foreground mb-2">Which modules use this approval type</p>
        <div className="flex flex-wrap gap-2">
          {APPLIES_TO_OPTIONS.map((opt) => (
            <label key={opt.value} className={`flex items-center gap-1.5 cursor-pointer rounded-md border px-2.5 py-1.5 text-xs transition-colors ${appliesTo.includes(opt.value) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-foreground/40'}`}>
              <input type="checkbox" className="sr-only" checked={appliesTo.includes(opt.value)}
                onChange={() => toggleAppliesTo(opt.value)} disabled={saving} />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Default First Reviewer" htmlFor="art-reviewer">
          <select id="art-reviewer" value={defaultFirstReviewer}
            onChange={(e) => setDefaultFirstReviewer(e.target.value as DefaultFirstReviewer | '')}
            disabled={saving}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            <option value="">— Not specified —</option>
            {FIRST_REVIEWER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Customer Approval" htmlFor="art-cust-appr">
          <select id="art-cust-appr" value={customerApprovalMode}
            onChange={(e) => setCustomerApprovalMode(e.target.value as CustomerApprovalMode)}
            disabled={saving}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            <option value="NOT_REQUIRED">Not Required</option>
            <option value="OPTIONAL">Optional</option>
            <option value="MANDATORY">Mandatory</option>
          </select>
        </FormField>
      </div>

      <FormField label="Portal Link Expiry (days)" htmlFor="art-expiry" error={errors.portalExpiryDays}>
        <Input id="art-expiry" type="number" min={1} max={365} value={portalExpiryDays}
          onChange={(e) => setPortalExpiryDays(e.target.value)} placeholder="14" disabled={saving} />
      </FormField>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Workflow Flags</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={requiresCommercialReview}
            onChange={(e) => {
              if (!requiresQuotation) setRequiresCommercialReview(e.target.checked);
            }}
            disabled={saving || requiresQuotation}
            className="rounded border-border"
            aria-describedby={requiresQuotation ? 'art-commercial-review-locked' : undefined}
          />
          Requires Commercial team review before customer is notified
        </label>
        {requiresQuotation && (
          <p
            id="art-commercial-review-locked"
            data-testid="art-commercial-review-locked"
            className="ml-6 text-xs text-muted-foreground"
          >
            Locked on: a kind that requires a quotation always requires Commercial review.
          </p>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={requiresQuotation}
            onChange={(e) => {
              const on = e.target.checked;
              setRequiresQuotation(on);
              if (on) setRequiresCommercialReview(true);
            }}
            disabled={saving}
            className="rounded border-border"
          />
          Requires a quotation — Commercial prepares and sends a quotation on the authorization case
        </label>
        {errors.requiresQuotation && (
          <p className="ml-6 text-xs text-destructive" role="alert">
            {errors.requiresQuotation}
          </p>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={allowsPartialApproval}
            onChange={(e) => setAllowsPartialApproval(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Allows partial approval (customer can approve a subset of items)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isBerType}
            onChange={(e) => setIsBerType(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          BER (Beyond Economic Repair) type — triggers irreversible write-off path
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={requiresConsent}
            onChange={(e) => setRequiresConsent(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Requires formal customer consent (e.g. BER write-off, condemn decisions)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={customerCanRequestModification}
            onChange={(e) => setCustomerCanRequestModification(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Customer may request modifications during the approval period
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={allowRoutingOverride}
            onChange={(e) => setAllowRoutingOverride(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Allow routing override (Owner/Admin/OPS may change the reviewer chain)
        </label>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function ApprovalRequestTypesPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ApprovalRequestType | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['approval-request-types', page, debouncedSearch],
    queryFn: () =>
      approvalRequestTypeService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['approval-request-types'] });

  const createMutation = useMutation({
    mutationFn: approvalRequestTypeService.create,
    onSuccess: () => { toast.success('Approval request type created'); setModalOpen(false); invalidate(); },
    onError: (e: unknown) => toast.error(extractMsg(e, 'Failed to create approval request type')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateApprovalRequestTypePayload> }) =>
      approvalRequestTypeService.update(id, payload),
    onSuccess: () => { toast.success('Approval request type updated'); setModalOpen(false); setEditTarget(null); invalidate(); },
    onError: (e: unknown) => toast.error(extractMsg(e, 'Failed to update approval request type')),
  });

  const deleteMutation = useMutation({
    mutationFn: approvalRequestTypeService.remove,
    onSuccess: () => { toast.success('Approval request type deleted'); invalidate(); },
    onError: () => toast.error('Failed to delete approval request type'),
  });

  const handleDelete = async (t: ApprovalRequestType) => {
    if (await confirmDelete(`${t.code} — ${t.name}`)) deleteMutation.mutate(t.id);
  };

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (t: ApprovalRequestType) => { setEditTarget(t); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Approval Request Types"
        description="Define approval categories used when routing jobs to customers or commercial review"
        onAdd={can('approval-request-type:create') ? openCreate : undefined}
        addLabel="Add Approval Type"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search by code or name..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[1200px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Code / Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Applies To</th>
                  <th className="px-4 py-3 font-medium text-foreground">First Reviewer</th>
                  <th className="px-4 py-3 font-medium text-foreground">Cust. Approval</th>
                  <th className="px-4 py-3 font-medium text-foreground">Routing Override</th>
                  <th className="px-4 py-3 font-medium text-foreground">BER</th>
                  <th className="px-4 py-3 font-medium text-foreground">Expiry</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div className="font-mono font-medium text-foreground text-xs">{t.code}</div>
                      <div className="text-foreground">{t.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      {t.appliesTo.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {t.appliesTo.map((a) => (
                            <span key={a} className="inline-flex rounded-full px-1.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                              {a.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {t.defaultFirstReviewer
                        ? FIRST_REVIEWER_OPTIONS.find((o) => o.value === t.defaultFirstReviewer)?.label ?? t.defaultFirstReviewer
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.customerApprovalMode === 'MANDATORY' ? 'bg-amber-100 text-amber-700' : t.customerApprovalMode === 'OPTIONAL' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {CUSTOMER_APPROVAL_LABELS[t.customerApprovalMode]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.allowRoutingOverride ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {t.allowRoutingOverride ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.isBerType ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground'}`}>
                        {t.isBerType ? 'BER' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-sm">{t.portalExpiryDays}d</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${t.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('approval-request-type:update') && (
                          <button onClick={() => openEdit(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${t.name}`}>
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('approval-request-type:delete') && (
                          <button onClick={() => handleDelete(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${t.name}`} disabled={deleteMutation.isPending}>
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faClipboardCheck}
          title={search ? 'No approval request types match your search' : 'No approval request types yet'}
          description={search ? 'Try a different search term.' : 'Add approval types such as Cost Approval, Beyond Economic Repair, Partial Repair.'}
          action={!search && can('approval-request-type:create') ? <Button onClick={openCreate}>Add Approval Type</Button> : undefined}
        />
      )}

      <Modal open={modalOpen} onClose={closeModal}
        title={editTarget ? 'Edit Approval Request Type' : 'Add Approval Request Type'} size="lg">
        <ApprovalRequestTypeForm
          initial={editTarget ? {
            code: editTarget.code, name: editTarget.name, description: editTarget.description ?? undefined,
            requiresCommercialReview: editTarget.requiresCommercialReview,
            requiresQuotation: editTarget.requiresQuotation,
            allowsPartialApproval: editTarget.allowsPartialApproval,
            isBerType: editTarget.isBerType, portalExpiryDays: editTarget.portalExpiryDays,
            requiresConsent: editTarget.requiresConsent,
            customerCanRequestModification: editTarget.customerCanRequestModification,
            appliesTo: editTarget.appliesTo,
            defaultFirstReviewer: editTarget.defaultFirstReviewer ?? undefined,
            customerApprovalMode: editTarget.customerApprovalMode,
            allowRoutingOverride: editTarget.allowRoutingOverride,
            sortOrder: editTarget.sortOrder,
          } : undefined}
          onSubmit={(p) => editTarget ? updateMutation.mutate({ id: editTarget.id, payload: p }) : createMutation.mutate(p)}
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
